import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { CompanyResultViewTypes } from 'src/app/core/enums/company-result-view.enum';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IOptions } from 'src/app/core/interfaces/common/common/options.interface';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IBusinessProfile } from 'src/app/core/interfaces/onboarding/business-profile/business-profile.interface';
import { IScoreRequest } from 'src/app/core/interfaces/onboarding/business-profile/score-request.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { selectCompanySearch } from 'src/app/core/store/onboarding/reducers+selectors/business-profile.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { watchTabletThreshold } from 'src/app/core/ui-helpers/functions/watch-tablet-threshold';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-confirm-company-search',
  templateUrl: './confirm-company-search.component.html',
  styleUrls: ['./confirm-company-search.component.scss'],
})
export class ConfirmCompanySearchComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  isTablet: boolean;
  confirmDetailsData: IOptions[] = [
    { id: 1, value: 'Confirm', selected: false },
  ];

  backState: PageState;
  nextState: PageState;
  skipState: PageState;
  homeState: PageState;
  bpState: PageState;
  fpState: PageState;
  cpState: PageState;
  bpSummaryState: PageState;
  form: FormGroup;
  displayData: any = [];
  searchResultData: IScoreRequest;

  questionText = 'Do you confirm this is your company?';
  properties$: Observable<any>;
  subscriptions: Subscription[] = [];
  payload: IBusinessProfile;
  companyNotFound: PageState;
  noScore: PageState;
  lowScore: PageState;
  industrySourceNotQualified: PageState;
  notQualified: PageState;
  preQualified: PageState;
  thisIsntMe = "This isn't me";
  pageStateId: string;

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private ees: EnrollmentExtendedService,
    private bf: BusinessProfileService,
    private store: Store<IAppState>,
    private router: Router
  ) {
    super();
    this.subscriptions.push(
      watchTabletThreshold((isTablet: boolean) => {
        this.isTablet = isTablet;
      })
    );
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            this.ees.updateBusinessProfileStateId(
              params['stateId'],
              this.homeState.id
            );
            this.pageStateId = params['stateId'];
          })
        );
      })
    );

    // Get and update payload with display data
    this.properties$ = this.store.select(selectCompanySearch);
    this.subscriptions.push(
      this.properties$.subscribe((props) => {
        this.displayData = {
          RegisteredCompanyName:
            props?.companySearch?.registeredCompanyName || '',
          Address1: props?.companySearch?.address1 || '',
          Address2: props?.companySearch?.address2 || '',
          State: props?.companySearch?.state || '',
          City: props?.companySearch?.city || '',
          ZipCode: props?.companySearch?.zipCode || '',
        };
        this.searchResultData = {
          IsConfirmed: true,
          LookupId: props?.companySearch?.lookupId,
          EntityNumber: props?.companySearch?.entityNumber,
        };
      })
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async submit() {
    //
    // const res = await this.bf.getBestMatch();
    //
    // return true;
  }
  populatePageStates(transitions: ITransition[]): void {
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
    this.bpState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BUSINESS_PROFILE,
      transitions
    );
    this.companyNotFound = this.ps.getPageStateByEventAndTransitions(
      EventTypes.COMPANY_NOT_FOUND,
      transitions
    );
    this.noScore = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NO_SCORE,
      transitions
    );
    this.lowScore = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BELOW_SBFE_THRESHOLD,
      transitions
    );
    this.industrySourceNotQualified = this.ps.getPageStateByEventAndTransitions(
      EventTypes.INDUSTRY_SOURCE_NOT_QUALIFIED,
      transitions
    );
    this.notQualified = this.ps.getPageStateByEventAndTransitions(
      EventTypes.COMPANY_NOT_QUALIFIED,
      transitions
    );
    this.preQualified = this.ps.getPageStateByEventAndTransitions(
      EventTypes.PRE_QUALIFIED,
      transitions
    );
    this.bpSummaryState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.APPLICATION_SUMMARY,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.questionText =
      contentDict.get(ContentIDConstants.Question)?.displayText ??
      this.questionText;
  }

  private buildDisplayPayload() {
    this.displayData = {
      'Legal Form Of Business Name': this.payload.LegalFormOfBusinessName,
      'State Of Organization': this.payload.StateOfOrganization,
      'Annual Revenue': this.payload.AnnualRevenue,
      'Year Founded': this.payload.YearFounded,
      Website: this.payload.Website,
      'Number Of Employees': this.payload.NumberOfEmployees,
      DBA: this.payload.DBA,
      Industry: this.payload.IndustryBusinessId,
      Address: `${this.payload.Address1},${this.payload.Address2},${this.payload.City},${this.payload.State},${this.payload.ZipCode}`,
      'Invoicing Method': this.payload.InvoicingMethod,
    };
  }

  async confirmClick() {
    // Call api with lookup id etc
    const result = await this.bf.confirmCompanyInfo(this.searchResultData);

    if (result != null) {
      switch (result) {
        case CompanyResultViewTypes.PreQualified:
          await Promise.allSettled([
            this.ees.updateBusinessProfileStateId(
              this.bpSummaryState.id,
              this.preQualified.id
            ),
            this.ees.completeBusinessProfile(),
            this.ees.activateFinancialProfile(),
          ]);
          this.router.navigate([this.preQualified.pageUrl], {
            queryParams: { stateId: this.preQualified.id },
          });
          break;
        case CompanyResultViewTypes.LowScore:
          await Promise.allSettled([
            this.ees.updateBusinessProfileStateId(
              this.bpSummaryState.id,
              this.lowScore.id
            ),
            this.ees.completeBusinessProfile(),
          ]);
          this.router.navigate([this.lowScore.pageUrl], {
            queryParams: { stateId: this.lowScore.id },
          });
          break;
        case CompanyResultViewTypes.NoScore:
          await Promise.allSettled([
            this.ees.updateBusinessProfileStateId(
              this.bpSummaryState.id,
              this.noScore.id
            ),
            this.ees.completeBusinessProfile(),
          ]);
          this.router.navigate([this.noScore.pageUrl], {
            queryParams: { stateId: this.noScore.id },
          });
          break;
        case CompanyResultViewTypes.IndustrySourceNotQualified:
          await Promise.allSettled([
            this.ees.updateBusinessProfileStateId(
              this.bpSummaryState.id,
              this.industrySourceNotQualified.id
            ),
            this.ees.completeBusinessProfile(),
          ]);
          this.router.navigate([this.industrySourceNotQualified.pageUrl], {
            queryParams: { stateId: this.industrySourceNotQualified.id },
          });
          break;
        case CompanyResultViewTypes.NotQualified:
          await Promise.allSettled([
            this.ees.updateBusinessProfileStateId(
              this.bpSummaryState.id,
              this.notQualified.id
            ),
            this.ees.completeBusinessProfile(),
          ]);
          this.router.navigate([this.notQualified.pageUrl], {
            queryParams: { stateId: this.notQualified.id },
          });
          break;
        default:
          await Promise.allSettled([
            this.ees.updateBusinessProfileStateId(
              this.bpSummaryState.id,
              this.notQualified.id
            ),
            this.ees.completeBusinessProfile(),
          ]);
          this.router.navigate([this.notQualified.pageUrl], {
            queryParams: { stateId: this.notQualified.id },
          });
          break;
      }
    }

    return true;
  }

  async thisIsntMeClick() {
    this.router.navigate([this.companyNotFound.pageUrl], {
      queryParams: { stateId: this.companyNotFound.id },
    });
  }
}
